import React from 'react'
import { Tooltip, Typography } from 'antd'
import formatCurrency from 'components/IntlCurrencyInput/format'

enum ReconciliationStatus {
  PAID = 'PAID',
  UNPAID = 'UNPAID',
  UNDERPAID = 'UNDERPAID',
  OVERPAID = 'OVERPAID',
}

interface ClearCostProps {
  clearedCost: number
  planCost: number
  toleranceThreshold?: number
}

/**
 * Determines the reconciliation status of a payment.
 */
export const determineReconciliationStatus = (
  totalAmount: number,
  carrierExpected: number | string | null,
  surecoExpected: number | string | null,
  toleranceThreshold: number
): ReconciliationStatus => {
  const numberCarrierExpected = Number(carrierExpected)
  const numberSurecoExpected = Number(surecoExpected)
  const expectedAmount = numberCarrierExpected || numberSurecoExpected || 0
  const difference = Math.abs(totalAmount - expectedAmount)

  if (difference <= toleranceThreshold) {
    return ReconciliationStatus.PAID
  }

  if (totalAmount === 0) {
    return ReconciliationStatus.UNPAID
  }

  if (totalAmount < expectedAmount) {
    return ReconciliationStatus.UNDERPAID
  }

  return ReconciliationStatus.OVERPAID
}

const statusColors: Record<ReconciliationStatus, string> = {
  [ReconciliationStatus.PAID]: '#52c41a', // green
  [ReconciliationStatus.UNPAID]: '#afafaf', // gray
  [ReconciliationStatus.UNDERPAID]: '#faad14', // orange
  [ReconciliationStatus.OVERPAID]: '#f5222d', // red
}

const statusTooltips: Record<ReconciliationStatus, string> = {
  [ReconciliationStatus.PAID]: 'Cleared cost matches the plan cost.',
  [ReconciliationStatus.UNPAID]: 'No payment has been made.',
  [ReconciliationStatus.UNDERPAID]: 'Cleared cost is less than the plan cost.',
  [ReconciliationStatus.OVERPAID]: 'Cleared cost exceeds the plan cost.',
}

export default function ClearCost({
  clearedCost,
  planCost,
  toleranceThreshold = 0.8,
}: ClearCostProps) {
  if (!clearedCost || clearedCost === 0) {
    return <span>-</span>
  }

  const status = determineReconciliationStatus(
    clearedCost,
    planCost,
    null,
    toleranceThreshold
  )

  if (
    status === ReconciliationStatus.UNDERPAID ||
    status === ReconciliationStatus.OVERPAID
  ) {
    return (
      <Tooltip title={statusTooltips[status]}>
        <Typography.Text
          style={{ color: statusColors[status], fontWeight: 700 }}
        >
          {formatCurrency(clearedCost)}
        </Typography.Text>
      </Tooltip>
    )
  }

  return <div style={{ fontWeight: 700 }}>{formatCurrency(clearedCost)}</div>
}
